// Match the backend's numbered BODY slots. Header/button variables have their
// own contracts and are deliberately excluded here.
const MAX_SLOT = 2147483647;

export function bodyVariableSlots(text = "") {
  const slots = new Set();
  for (const match of String(text).matchAll(/\{\{(\d+)\}\}/g)) {
    const slot = Number(match[1]);
    if (Number.isInteger(slot) && slot > 0 && slot <= MAX_SLOT) slots.add(slot);
  }
  return [...slots].sort((a, b) => a - b);
}

export function normalizeBodyExamples(examples) {
  const entries = Array.isArray(examples)
    ? examples.map((value, index) => [String(index + 1), value])
    : Object.entries(examples || {});
  return Object.fromEntries(entries.filter(([key]) => /^\d+$/.test(key) && Number(key) > 0 && Number(key) <= MAX_SLOT)
    .map(([key, value]) => [String(Number(key)), String(value ?? "")]));
}

export function nextBodyVariable(text) {
  const slots = bodyVariableSlots(text);
  const next = (slots.at(-1) || 0) + 1;
  return next <= MAX_SLOT ? `{{${next}}}` : null;
}

export function bodyVariableError(text, examples) {
  const body = String(text || "");
  let malformed = false;
  const rest = body.replace(/\{\{(\d+)\}\}/g, (match, digits, offset) => {
    const slot = Number(digits);
    if (!Number.isInteger(slot) || slot < 1 || slot > MAX_SLOT || body[offset - 1] === "{" || body[offset + match.length] === "}") {
      malformed = true;
      return match;
    }
    return "";
  });
  if (malformed || /\{\{|\}\}/.test(rest)) {
    return "Use numbered body variables such as {{1}}. Named, zero, spaced or malformed placeholders are not supported.";
  }
  const slots = bodyVariableSlots(body);
  const missing = [];
  let expected = 1;
  for (const slot of slots) {
    while (expected < slot && missing.length < 5) missing.push(`{{${expected++}}}`);
    if (missing.length >= 5) break;
    expected = slot + 1;
  }
  if (missing.length) {
    return `Missing body slots: ${missing.join(", ")}. Number variables continuously from {{1}}; existing samples keep their variable numbers.`;
  }
  const values = normalizeBodyExamples(examples);
  const missingExamples = slots.filter(slot => !values[String(slot)]?.trim());
  return missingExamples.length ? `Examples required for placeholders: ${missingExamples.map(slot => `{{${slot}}}`).join(", ")}` : null;
}

export function bodyExamplesForApi(text, examples) {
  const values = normalizeBodyExamples(examples);
  return Object.fromEntries(bodyVariableSlots(text).map(slot => [String(slot), (values[String(slot)] || "").trim()]));
}
